import React, { useState, useEffect } from 'react';
import { formatPlainNumber } from '../formatting.js';

/* Safe arithmetic evaluator — allows digits, + - * / ( ) . % and whitespace.
   Commas are stripped (they're presentational thousand separators). The
   expression runs in strict-mode isolated Function scope with no globals,
   so there's no path for the user to execute arbitrary code even if they
   somehow bypassed the character whitelist. */
export function evalExpression(raw) {
  if (raw == null) return { ok: false };
  const s = String(raw).trim();
  if (!s) return { ok: true, value: 0 };
  if (!/^[\d+\-*/().%\s,]+$/.test(s)) return { ok: false };
  const clean = s.replace(/,/g, '');
  try {
    // eslint-disable-next-line no-new-func
    const v = Function(`"use strict"; return (${clean});`)();
    if (!Number.isFinite(v)) return { ok: false };
    return { ok: true, value: v };
  } catch {
    return { ok: false };
  }
}

export function formatDisplay(v, decimals) {
  if (v == null || v === '') return '';
  if (!Number.isFinite(v)) return '';
  return formatPlainNumber(v, { decimals, scale: false });
}

/* Core hook — arithmetic-capable numeric field. Returns a prop bundle
   spreadable onto any <input>. Used by <NumberInput> (labelled form field)
   and <NumericField> (bare input for dense table/grid cells).

   Features:
     - Type an expression ("1000+500"); evaluates on blur
     - Thousand-separated display when not focused (1,500.00)
     - Raw value shown on focus for easy editing
     - Wheel-scroll blurs to prevent accidental increments
     - Enter blurs to commit
     - Clamps to min/max if provided
     - Red ring on invalid expression (reverts to last good value after 900ms) */
export function useNumericField(value, onChange, { decimals = 2, min, max } = {}) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => formatDisplay(value, decimals));
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!focused) setText(formatDisplay(value, decimals));
  }, [value, decimals, focused]);

  return {
    type: 'text',
    inputMode: 'decimal',
    value: text,
    title: 'Type a number or arithmetic expression (e.g. 1000 + 500 * 1.2)',
    onChange: (e) => { setText(e.target.value); if (error) setError(false); },
    onFocus: (e) => {
      setFocused(true);
      setText(value == null || value === '' ? '' : String(value));
      setTimeout(() => e.target.select?.(), 0);
    },
    onBlur: () => {
      setFocused(false);
      const { ok, value: v } = evalExpression(text);
      if (!ok) {
        setError(true);
        setTimeout(() => { setError(false); setText(formatDisplay(value, decimals)); }, 900);
        return;
      }
      let clamped = v;
      if (typeof min === 'number' && clamped < min) clamped = min;
      if (typeof max === 'number' && clamped > max) clamped = max;
      setText(formatDisplay(clamped, decimals));
      onChange?.(clamped);
    },
    onKeyDown: (e) => { if (e.key === 'Enter') e.currentTarget.blur(); },
    onWheel: (e) => e.currentTarget.blur(),
    'data-error': error || undefined,
  };
}

/* Compact, label-less numeric field for dense table/grid cells. Has the
   same arithmetic + formatting as <NumberInput>, but renders just an
   <input> so it drops into fixed-width grid columns without the labelled
   form layout. */
export function NumericField({ value, onChange, decimals = 2, min, max, className = '', ...rest }) {
  const field = useNumericField(value, onChange, { decimals, min, max });
  const errCls = field['data-error'] ? 'border-accent-red ring-1 ring-accent-red/40' : '';
  return <input className={`${className} ${errCls}`} {...field} {...rest} />;
}

/* NumberInput — labelled numeric form field. Uses useNumericField under
   the hood so typing "1000+500" and tabbing out evaluates to 1,500.00. */
export default function NumberInput({ label, value, onChange, min, max, step = 1, prefix, suffix, hint, width, decimals = 2 }) {
  const field = useNumericField(value, onChange, { decimals, min, max });
  const error = !!field['data-error'];
  return (
    <label className="flex flex-col gap-[0.3rem] py-[0.45rem]" style={width ? { width } : undefined}>
      <span className="text-[0.82rem] text-text flex justify-between items-baseline gap-[0.4rem]">
        {label}
        {hint && <span className="text-[0.73rem] text-text-dim">{hint}</span>}
      </span>
      <span className={`flex items-center bg-surface-raised border rounded-sm overflow-hidden transition-colors ${error ? 'border-accent-red ring-1 ring-accent-red/40' : 'border-border focus-within:border-accent-blue focus-within:ring-1 focus-within:ring-accent-blue/30'}`}>
        {prefix && <span className="px-[0.55rem] py-[0.4rem] text-[0.82rem] text-text-muted bg-surface border-r border-border">{prefix}</span>}
        <input
          className="bg-transparent text-text border-0 px-[0.55rem] py-[0.4rem] w-full font-[inherit] text-[0.85rem] tabular-nums text-right focus:outline-none"
          {...field}
        />
        {suffix && <span className="px-[0.55rem] py-[0.4rem] text-[0.82rem] text-text-muted bg-surface border-r border-border">{suffix}</span>}
      </span>
    </label>
  );
}
